/**
 * Author:       Sean Johnson <sean@scj.net>
 * Purpose:      Single registry of every site and app shown in the auth.scj.net launcher.
 *               Add, remove or rename a destination here — nowhere else.
 * Last updated: 2026-10-18
 */

/** How the destination authenticates the visitor. */
export type AppAuth = 'sso' | 'gitlab' | 'own'

export interface AppEntry {
  name: string
  url: string
  description: string
  auth: AppAuth
  /** Cognito group required to see the entry. Admins see everything. */
  group: string
}

export interface AppSection {
  title: string
  note: string
  apps: AppEntry[]
}

export const APP_SECTIONS: AppSection[] = [
  {
    title: 'Apps',
    note: 'Single sign-on with this account',
    apps: [
      { name: 'Trading Journal', url: 'https://trading.scj.net', description: 'COLE journal and trade review', auth: 'sso', group: 'app:trading' },
      { name: 'Workout Tracker', url: 'https://workout.scj.net', description: 'Training log', auth: 'sso', group: 'app:workout' },
      { name: 'SCJ Mail Dashboard', url: 'https://mdash.scj.net', description: 'Mail infrastructure dashboard', auth: 'sso', group: 'app:mdash' },
      { name: 'Auto Tracker', url: 'https://auto.scj.net', description: 'Vehicle tracking', auth: 'sso', group: 'app:auto' },
    ],
  },
  {
    title: 'SeanOS',
    note: 'Knowledge sites — GitLab login',
    apps: [
      { name: 'SeanOS Wiki', url: 'https://seanos.app', description: 'Principles, mental models and knowledge', auth: 'gitlab', group: 'admin' },
      { name: 'Recipes', url: 'https://recipes.scj.net', description: 'Recipes and technique', auth: 'gitlab', group: 'admin' },
      { name: 'Decisions', url: 'https://decision.scj.net', description: 'Decision records', auth: 'gitlab', group: 'admin' },
      { name: 'Work KB', url: 'https://kb.insidedesk.pro', description: 'InsideDesk knowledge base', auth: 'gitlab', group: 'admin' },
    ],
  },
  {
    title: 'Projects',
    note: 'Separate login per app',
    apps: [
      { name: 'Carnivia', url: 'https://carnivia.seanos.app', description: 'Carnival build tracker', auth: 'own', group: 'admin' },
      { name: 'Spanish', url: 'https://spanish.seanos.app', description: 'Spanish learning tracker', auth: 'own', group: 'admin' },
    ],
  },
]

/** Sections filtered to what this user may see; empty sections are dropped. */
export function visibleSections(groups: string[], isAdmin: boolean): AppSection[] {
  const canSee = (app: AppEntry) => isAdmin || groups.includes(app.group)
  return APP_SECTIONS
    .map((section) => ({ ...section, apps: section.apps.filter(canSee) }))
    .filter((section) => section.apps.length > 0)
}
